import React, { useState, useEffect } from 'react';
import { Send, Users, User, Search, Filter, Trash2, Star, Archive, MessageSquare, Bell } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const SafeListMessaging = ({ userRole = 'member' }) => {
  const [messages, setMessages] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock recipient options based on user role
  const recipientOptions = userRole === 'admin' ? [
    { value: 'all_members', label: '📢 All Members (Broadcast)', type: 'broadcast' },
    { value: 'free_tier', label: '👥 Boot Camp Members', type: 'group' },
    { value: 'basic_tier', label: '👥 Rookie Members', type: 'group' },
    { value: 'pro_tier', label: '👥 Pro Members', type: 'group' },
    { value: 'enterprise_tier', label: '👥 Enterprise Members', type: 'group' },
    { value: 'active_members', label: '✅ Active Members Only', type: 'group' },
    { value: 'demo@bbcquicklist.com', label: '👤 Demo User', type: 'individual' },
  ] : [
    { value: 'admin@bbcquicklist.com', label: '🛡️ Support Team', type: 'support' },
    { value: 'demo@bbcquicklist.com', label: '👤 Demo User', type: 'member' },
    // Members can only message admin/support and other specific members
  ];

  // Mock messages data
  const mockMessages = [
    {
      id: 1,
      from: 'admin@bbcquicklist.com',
      to: 'all_members',
      subject: 'Welcome to BBC Quick List!',
      message: 'Welcome to our internal communication system. Use this safe list to communicate with team members and get support.',
      timestamp: new Date(Date.now() - 86400000),
      type: 'broadcast',
      priority: 'high',
      read: false
    },
    {
      id: 2,
      from: 'demo@bbcquicklist.com',
      to: 'admin@bbcquicklist.com',
      subject: 'Question about commissions',
      message: 'Hi, I have a question about when commissions are paid out. Can you help?',
      timestamp: new Date(Date.now() - 3600000),
      type: 'support',
      priority: 'normal',
      read: true
    },
    {
      id: 3,
      from: 'admin@bbcquicklist.com',
      to: 'basic_tier',
      subject: 'New Training Materials Available',
      message: 'New training materials have been added to your account. Check them out in the Boot Camp section.',
      timestamp: new Date(Date.now() - 7200000),
      type: 'group',
      priority: 'normal',
      read: true
    }
  ];

  useEffect(() => {
    setMessages(mockMessages);
  }, []);

  const handleSendMessage = async () => {
    if (!selectedRecipient || !messageText.trim()) {
      toast.error('Please select a recipient and enter a message');
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMessage = {
        id: messages.length + 1,
        from: userRole === 'admin' ? 'admin@bbcquicklist.com' : 'member@bbcquicklist.com',
        to: selectedRecipient,
        subject: 'New Message',
        message: messageText,
        timestamp: new Date(),
        type: recipientOptions.find(r => r.value === selectedRecipient)?.type || 'individual',
        priority: 'normal',
        read: false
      };

      setMessages([newMessage, ...messages]);
      setMessageText('');
      setSelectedRecipient('');
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'broadcast': return '📢';
      case 'group': return '👥';
      case 'support': return '🛡️';
      default: return '💬';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredMessages = messages.filter(message => {
    if (filter !== 'all' && message.type !== filter) return false;
    if (searchTerm && !message.subject.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !message.message.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safe List Communications</h1>
          <p className="text-gray-600">
            {userRole === 'admin' ? 'Send messages to members and manage communications' : 'Communicate with support and team members'}
          </p>
        </div>
        <Badge className="bg-green-100 text-green-800">
          <MessageSquare className="h-3 w-3 mr-1" />
          {messages.filter(m => !m.read).length} Unread
        </Badge>
      </div>

      {/* Compose Message */}
      <Card className="border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Send className="h-5 w-5" />
            Compose New Message
          </CardTitle>
          <CardDescription className="text-blue-600">
            Send a message through the safe list communication system
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {userRole === 'admin' ? 'Send To' : 'Recipient'}
            </label>
            <Select value={selectedRecipient} onValueChange={setSelectedRecipient}>
              <SelectTrigger>
                <SelectValue placeholder="Select recipient..." />
              </SelectTrigger>
              <SelectContent>
                {recipientOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-500">
                {messageText.length}/1000 characters
              </span>
              {userRole === 'admin' && selectedRecipient.includes('_tier') && (
                <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                  Group Message
                </Badge>
              )}
            </div>
          </div>

          <Button 
            onClick={handleSendMessage} 
            disabled={loading || !selectedRecipient || !messageText.trim()}
            className="btn-primary w-full"
          >
            <Send className="h-4 w-4 mr-2" />
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </CardContent>
      </Card>

      {/* Message Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Message History
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="broadcast">Broadcasts</SelectItem>
                  <SelectItem value="group">Groups</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No messages found</p>
              </div>
            ) : (
              filteredMessages.map((message) => (
                <div
                  key={message.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    message.read ? 'bg-white' : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-lg">{getMessageIcon(message.type)}</span>
                        <div>
                          <h4 className={`font-medium ${!message.read ? 'font-bold text-blue-900' : 'text-gray-900'}`}>
                            {message.subject}
                          </h4>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span>From: {message.from}</span>
                            <span>•</span>
                            <span>To: {message.to}</span>
                            <span>•</span>
                            <span>{message.timestamp.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {message.message}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <Badge className={getPriorityColor(message.priority)}>
                        {message.priority}
                      </Badge>
                      {!message.read && (
                        <Badge className="bg-red-100 text-red-800">
                          <Bell className="h-3 w-3 mr-1" />
                          New
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Admin Tools */}
      {userRole === 'admin' && (
        <Card className="border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <Users className="h-5 w-5" />
              Admin Communication Tools
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button variant="outline" className="flex items-center justify-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Member Directory</span>
              </Button>
              <Button variant="outline" className="flex items-center justify-center space-x-2">
                <Star className="h-4 w-4" />
                <span>Message Templates</span>
              </Button>
              <Button variant="outline" className="flex items-center justify-center space-x-2">
                <Archive className="h-4 w-4" />
                <span>Message Archive</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SafeListMessaging;